/**
 * Report figures — browser port of the native OpenADS matplotlib PNGs the
 * WASM pipeline never produced:
 *
 *  - `<sid>_DWIstroke.png` / `<sid>_DWIstroke_space-MNI152.png`
 *    (`ads.reporting.visualization.dwi_visualization.create_visualization_compare_orig`
 *    / `create_visualization_compare`)
 *  - `<sid>_HP.png` / `<sid>_HP_space-MNI152.png`
 *    (`ads.reporting.visualization.pwi_visualization.create_hp_visualization_compare_orig`
 *    / `create_hp_visualization_compare`)
 *
 * Layout is computed with matplotlib's own geometry rather than eyeballed:
 * figure size in inches, subplot params, GridSpec wspace/hspace (negative
 * spacing in the DWI figure means rows overlap and later rows paint over
 * earlier ones, exactly as matplotlib draws them), `aspect='equal'` axes
 * shrunk around the image with the default 'C' anchor, titles, then
 * `bbox_inches="tight"` cropping with 0.1in padding at dpi=150. Only the
 * font rasteriser differs (browser font vs DejaVu Sans).
 *
 * All volumes are `NiftiVolume`s as returned by `loadNiftiFile` (RAS-
 * canonical, matching the native `nib.as_closest_canonical` loads).
 *
 * QC panels live in `qc.ts`; `renderQcFigure` is re-exported here so every
 * PNG renderer can be imported from one place.
 */
import type { NiftiVolume } from "../nifti";
import {
  DPI,
  FONT_FAMILY,
  GRAY_LUT,
  JET_LUT,
  axialSlice,
  canvasToPng,
  colorize,
  context2d,
  createCanvas,
  displayRange,
  blitRgba,
  findContours,
  finiteMinMax,
  measureText,
  resampleSlice,
  sameShape,
  selectCenterSlices,
  strokeContours,
  type Contour,
  type Lut,
  type Slice2D,
} from "./figureUtils";


// ---------------------------------------------------------------------------
// Minimal matplotlib figure model
// ---------------------------------------------------------------------------

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Overlay {
  contours: Contour[];
  color: string;
  linewidth: number;
  alpha?: number;
}

interface Panel {
  /** Axes box in inches, figure top-left origin. */
  box: Rect;
  slice: Slice2D;
  lut: Lut;
  vmin: number;
  vmax: number;
  overlays: Overlay[];
}

interface FigText {
  text: string;
  fontPt: number;
  /** Anchor x (inches) — text is horizontally centred on it. */
  x: number;
  /** Baseline y (inches). */
  baseline: number;
  width: number;
  ascent: number;
  descent: number;
}

/**
 * GridSpec cell rectangles (inches, top-left origin) — matplotlib's
 * `GridSpecBase.get_grid_positions`: cell = total / (n + space*(n-1)),
 * separator = space * cell, rows laid out from the top.
 */
function gridCells(
  figW: number,
  figH: number,
  p: { left: number; right: number; bottom: number; top: number; wspace: number; hspace: number },
  nRows: number,
  nCols: number
): Rect[][] {
  const totW = (p.right - p.left) * figW;
  const totH = (p.top - p.bottom) * figH;
  const cellW = totW / (nCols + p.wspace * (nCols - 1));
  const cellH = totH / (nRows + p.hspace * (nRows - 1));
  const x0 = p.left * figW;
  const y0 = (1 - p.top) * figH;
  const rows: Rect[][] = [];
  for (let r = 0; r < nRows; r++) {
    const row: Rect[] = [];
    for (let c = 0; c < nCols; c++) {
      row.push({ x: x0 + c * cellW * (1 + p.wspace), y: y0 + r * cellH * (1 + p.hspace), w: cellW, h: cellH });
    }
    rows.push(row);
  }
  return rows;
}

/** `aspect='equal'`, adjustable='box', anchor 'C': shrink the cell to the image's aspect, centred. */
function fitBox(cell: Rect, imgW: number, imgH: number): Rect {
  const a = imgH / imgW;
  if (cell.h / cell.w > a) {
    const h = cell.w * a;
    return { x: cell.x, y: cell.y + (cell.h - h) / 2, w: cell.w, h };
  }
  const w = cell.h / a;
  return { x: cell.x + (cell.w - w) / 2, y: cell.y, w, h: cell.h };
}

let measureCtx: ReturnType<typeof context2d> | null = null;
function makeText(text: string, fontPt: number, x: number, baseline: number): FigText {
  measureCtx ??= context2d(createCanvas(8, 8));
  const m = measureText(measureCtx, text, fontPt * (DPI / 72), "bold");
  return { text, fontPt, x, baseline, width: m.width / DPI, ascent: m.ascent / DPI, descent: m.descent / DPI };
}

/** Draws panels + texts onto a canvas cropped like `savefig(bbox_inches="tight", pad_inches=0.1)`. */
async function renderFigure(panels: Panel[], texts: FigText[], padIn = 0.1): Promise<Blob> {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const grow = (x0: number, y0: number, x1: number, y1: number) => {
    minX = Math.min(minX, x0); minY = Math.min(minY, y0);
    maxX = Math.max(maxX, x1); maxY = Math.max(maxY, y1);
  };
  for (const p of panels) grow(p.box.x, p.box.y, p.box.x + p.box.w, p.box.y + p.box.h);
  for (const t of texts) grow(t.x - t.width / 2, t.baseline - t.ascent, t.x + t.width / 2, t.baseline + t.descent);
  minX -= padIn; minY -= padIn; maxX += padIn; maxY += padIn;

  const canvas = createCanvas((maxX - minX) * DPI, (maxY - minY) * DPI);
  const ctx = context2d(canvas);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const px = (xIn: number) => (xIn - minX) * DPI;
  const py = (yIn: number) => (yIn - minY) * DPI;

  for (const p of panels) {
    // Agg snaps images to whole pixels; doing the same avoids hairline seams where the
    // DWI figure's negative-hspace rows overlap (later axes paint over earlier ones).
    const x0 = Math.round(px(p.box.x)), y0 = Math.round(py(p.box.y));
    const rect = { x: x0, y: y0, w: Math.round(px(p.box.x + p.box.w)) - x0, h: Math.round(py(p.box.y + p.box.h)) - y0 };
    if (rect.w > 0 && rect.h > 0) blitRgba(ctx, colorize(resampleSlice(p.slice, rect.w, rect.h), p.lut, p.vmin, p.vmax), rect.w, rect.h, rect.x, rect.y);
    for (const o of p.overlays) strokeContours(ctx, o.contours, p.slice.width, p.slice.height, rect, o.color, o.linewidth, o.alpha);
  }
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  for (const t of texts) {
    ctx.font = `bold ${t.fontPt * (DPI / 72)}px ${FONT_FAMILY}`;
    ctx.fillText(t.text, px(t.x), py(t.baseline));
  }
  return canvasToPng(canvas);
}

// ---------------------------------------------------------------------------
// DWI stroke figure
// ---------------------------------------------------------------------------

export interface DwiStrokeFigureOptions {
  /**
   * Background DWI (b1000).
   *  - space "native": DWI/preprocess `<sid>_DWI_brain.nii.gz` (skull-stripped
   *    DWI), falling back to `<sid>_DWI.nii.gz` when no brain-extracted copy
   *    exists — native prefers `_brain`.
   *  - space "MNI152": DWI/registration `<sid>_DWI_space-MNI152_aff.nii.gz`.
   */
  dwi: NiftiVolume;
  /**
   * ADC, same space/grid as `dwi`.
   *  - "native": DWI/preprocess `<sid>_ADC_brain.nii.gz` (fallback `<sid>_ADC.nii.gz`).
   *  - "MNI152": DWI/registration `<sid>_ADC_space-MNI152_aff.nii.gz`.
   */
  adc: NiftiVolume;
  /**
   * Manual/ground-truth stroke label, or null when the subject has none.
   *  - "native": DWI/preprocess `<sid>_stroke.nii.gz`.
   *  - "MNI152": DWI/registration `<sid>_stroke_space-MNI152_aff.nii.gz`.
   * null -> 3-column figure (no "Stroke Label" column), exactly as native.
   */
  label: NiftiVolume | null;
  /**
   * Predicted stroke mask; also drives slice selection (native default
   * `use_prediction_for_slices=True`).
   *  - "native": DWI/segment `<sid>_stroke-mask.nii.gz` (must share `dwi`'s
   *    grid — native refuses to draw on a shape mismatch, so this throws).
   *  - "MNI152": DWI/segment `<sid>_stroke-mask_space-MNI152.nii.gz`.
   */
  pred: NiftiVolume;
  space: "native" | "MNI152";
}

/**
 * `<sid>_DWIstroke.png` (space "native" -> DWI/reporting/) or
 * `<sid>_DWIstroke_space-MNI152.png` (space "MNI152" -> DWI/reporting/).
 *
 * One row per selected axial slice (prediction-containing slices, up to 21
 * nearest the middle, plus every 20th slice); columns DWI | ADC |
 * [DWI + label contour (blue)] | DWI + prediction contour (red); each image
 * autoscaled to its slice min/max in gray.
 */
export async function renderDwiStrokeFigure(opts: DwiStrokeFigureOptions): Promise<Blob> {
  const { dwi, adc, pred, space } = opts;
  let label = opts.label;
  if (!sameShape(dwi, pred)) {
    throw new Error(`DWIstroke figure: shape mismatch DWI ${dwi.dims.join("x")} vs prediction ${pred.dims.join("x")}`);
  }
  if (!sameShape(dwi, adc)) throw new Error(`DWIstroke figure: shape mismatch DWI ${dwi.dims.join("x")} vs ADC ${adc.dims.join("x")}`);
  if (label && !sameShape(dwi, label)) label = null;

  const slices = selectCenterSlices(pred, 21, 20);
  if (slices.length === 0) throw new Error("DWIstroke figure: no slices selected");

  const tag = space === "MNI152" ? ["Aff/MNI", "CH3/MNI"] : ["Orig", "CH3/Orig"];
  const titles = label
    ? [`DWI (${tag[0]})`, `ADC (${tag[0]})`, `Stroke Label (${tag[0]})`, `Stroke Pred (${tag[1]})`]
    : [`DWI (${tag[0]})`, `ADC (${tag[0]})`, `Stroke Pred (${tag[1]})`];
  const nCols = titles.length;
  const nRows = slices.length;

  // plt.subplots(figsize=(4*nc, 3.5*nr)) with default subplot params, then
  // plt.subplots_adjust(wspace=-0.1, hspace=-0.1).
  const figW = 4 * nCols;
  const figH = 3.5 * nRows;
  const cells = gridCells(figW, figH, { left: 0.125, right: 0.9, bottom: 0.11, top: 0.88, wspace: -0.1, hspace: -0.1 }, nRows, nCols);

  const panels: Panel[] = [];
  let firstRowBoxes: Rect[] = [];
  slices.forEach((k, r) => {
    const d = axialSlice(dwi, k);
    const [dLo, dHi] = finiteMinMax(d.data);
    const a = axialSlice(adc, k);
    const [aLo, aHi] = finiteMinMax(a.data);
    const predC = findContours(axialSlice(pred, k), 0.5);
    const cols: Omit<Panel, "box">[] = [
      { slice: d, lut: GRAY_LUT, vmin: dLo, vmax: dHi, overlays: [] },
      { slice: a, lut: GRAY_LUT, vmin: aLo, vmax: aHi, overlays: [] },
    ];
    if (label) cols.push({ slice: d, lut: GRAY_LUT, vmin: dLo, vmax: dHi, overlays: [{ contours: findContours(axialSlice(label, k), 0.5), color: "#0000ff", linewidth: 1 }] });
    cols.push({ slice: d, lut: GRAY_LUT, vmin: dLo, vmax: dHi, overlays: [{ contours: predC, color: "#ff0000", linewidth: 1 }] });
    const boxes = cols.map((_, c) => fitBox(cells[r][c], d.width, d.height));
    if (r === 0) firstRowBoxes = boxes;
    cols.forEach((col, c) => panels.push({ ...col, box: boxes[c] }));
  });

  // ax.set_title(fontsize=10, fontweight='bold'): centred, baseline 6pt (axes.titlepad) above the axes.
  const texts = titles.map((t, c) => makeText(t, 10, firstRowBoxes[c].x + firstRowBoxes[c].w / 2, firstRowBoxes[c].y - 6 / 72));
  return renderFigure(panels, texts);
}

// ---------------------------------------------------------------------------
// HP (hypoperfusion) figure
// ---------------------------------------------------------------------------

export interface HpFigureOptions {
  /**
   * TTP map (required).
   *  - "native": PWI/preprocess `<sid>_TTP.nii.gz` (PWI grid).
   *  - "MNI152": PWI/registration `<sid>_TTP_space-MNI152_aff_desc-norm.nii.gz`
   *    if produced, else `<sid>_TTP_space-MNI152_aff.nii.gz` (native prefers
   *    desc-norm; with per-slice percentile windowing either renders the same
   *    up to a linear intensity rescale).
   */
  ttp: NiftiVolume;
  /**
   * Predicted HP mask (required, same grid as `ttp`); drives slice selection.
   *  - "native": PWI/segmentation `<sid>_HP-mask.nii.gz`.
   *  - "MNI152": PWI/segmentation `<sid>_HP-mask_space-MNI152.nii.gz`.
   */
  hpPred: NiftiVolume;
  /**
   * Manual HP label (optional; dropped if its grid differs from `ttp`).
   *  - "native": PWI/preprocess `<sid>_HP_manual.nii.gz`.
   *  - "MNI152": PWI/registration `<sid>_HP_manual_space-MNI152_aff.nii.gz`.
   */
  hpLabel?: NiftiVolume | null;
  /**
   * DWI background.
   *  - "native": DWI/preprocess `<sid>_DWI_brain.nii.gz` (fallback `<sid>_DWI.nii.gz`).
   *    Optional — native only shows the three DWI columns when the native DWI
   *    grid equals the native TTP grid (it normally does not: e.g. 192x192x38
   *    vs 128x128x20), otherwise it silently renders a TTP-only figure.
   *  - "MNI152": PWI/registration (else DWI/registration)
   *    `<sid>_DWI_space-MNI152_aff_desc-norm.nii.gz`, else `..._aff.nii.gz`.
   *    Required in MNI space (native raises without it).
   */
  dwi?: NiftiVolume | null;
  /**
   * Manual stroke label (optional, dropped on a grid mismatch with `dwi`).
   *  - "native": DWI/preprocess `<sid>_stroke.nii.gz`.
   *  - "MNI152": DWI/registration `<sid>_stroke_space-MNI152_aff.nii.gz`.
   */
  strokeLabel?: NiftiVolume | null;
  /**
   * Predicted stroke mask (optional, dropped on a grid mismatch with `dwi`).
   *  - "native": DWI/segment `<sid>_stroke-mask.nii.gz`.
   *  - "MNI152": native searches DWI/segment for
   *    `<sid>_stroke-mask_space-MNI152_affsyn.nii.gz` first, then
   *    `..._space-MNI152_aff.nii.gz` — i.e. the SyN-space prediction. When the
   *    WASM run has no SyN output, pass null (or the affine-space
   *    `<sid>_stroke-mask_space-MNI152.nii.gz` if a stroke column is wanted).
   */
  strokePred?: NiftiVolume | null;
  space: "native" | "MNI152";
}

type HpKey = "dwi" | "dwi_stroke_manual" | "dwi_stroke_pred" | "ttp" | "ttp_hp_manual" | "ttp_hp_pred";

/**
 * `<sid>_HP.png` (space "native" -> PWI/reporting/) or
 * `<sid>_HP_space-MNI152.png` (space "MNI152" -> PWI/reporting/).
 *
 * Columns (each optional one only when its volume is present and on-grid):
 * DWI | DWI + Stroke Mask (red) | DWI + Stroke(Pred) (lime) | TTP (jet) |
 * TTP + HP(Manual) (white w/ black core) | TTP + HP(Pred) (lime). DWI is
 * windowed to its slice's 1–99th percentile, TTP to 5–95th. Rows: HP-pred
 * slices (up to 20 nearest the middle) plus every 20th slice.
 */
export async function renderHpFigure(opts: HpFigureOptions): Promise<Blob> {
  const { ttp, hpPred, space } = opts;
  let dwi = opts.dwi ?? null;
  let strokeLabel = opts.strokeLabel ?? null;
  let strokePred = opts.strokePred ?? null;
  let hpLabel = opts.hpLabel ?? null;

  if (!sameShape(ttp, hpPred)) throw new Error(`HP figure: TTP ${ttp.dims.join("x")} and HP pred ${hpPred.dims.join("x")} shape mismatch`);
  if (space === "MNI152") {
    if (!dwi) throw new Error("HP figure: DWI (MNI152) is required in MNI space");
    if (!sameShape(dwi, ttp)) throw new Error(`HP figure: DWI ${dwi.dims.join("x")} and TTP ${ttp.dims.join("x")} shape mismatch`);
  } else if (!dwi || !sameShape(dwi, ttp)) {
    // Native fallback: TTP-only panel when native DWI and TTP grids differ.
    dwi = null;
  }
  if (!dwi) { strokeLabel = null; strokePred = null; }
  if (dwi && strokeLabel && !sameShape(strokeLabel, dwi)) strokeLabel = null;
  if (dwi && strokePred && !sameShape(strokePred, dwi)) strokePred = null;
  if (hpLabel && !sameShape(hpLabel, ttp)) hpLabel = null;

  const slices = selectCenterSlices(hpPred, 20, 20);
  if (slices.length === 0) throw new Error("HP figure: no slices selected from HP prediction");

  const cols: [string, HpKey][] = [];
  if (dwi) {
    cols.push(["DWI", "dwi"]);
    if (strokeLabel) cols.push(["DWI + Stroke Mask", "dwi_stroke_manual"]);
    if (strokePred) cols.push(["DWI + Stroke(Pred)", "dwi_stroke_pred"]);
  }
  cols.push(["TTP", "ttp"]);
  if (hpLabel) cols.push(["TTP + HP(Manual)", "ttp_hp_manual"]);
  cols.push(["TTP + HP(Pred)", "ttp_hp_pred"]);

  const nCols = cols.length;
  const nRows = slices.length;
  const figW = 3.1 * nCols;
  const figH = 2.6 * nRows;
  // GridSpec(left=.02, right=.98, bottom=.02, top=.94, wspace=.01, hspace=.02). The trailing
  // plt.tight_layout() cannot override a GridSpec's explicit params, so these are final.
  const cells = gridCells(figW, figH, { left: 0.02, right: 0.98, bottom: 0.02, top: 0.94, wspace: 0.01, hspace: 0.02 }, nRows, nCols);

  // `len(contour) > 5` filter from the native plotting loops.
  const contoursOf = (vol: NiftiVolume, k: number) => findContours(axialSlice(vol, k), 0.5).filter((c) => c.length > 5);

  const panels: Panel[] = [];
  for (let r = 0; r < nRows; r++) {
    const k = slices[r];
    const t = axialSlice(ttp, k);
    const [tLo, tHi] = displayRange(t.data, 5, 95);
    let d: Slice2D | null = null;
    let dLo = 0, dHi = 1;
    if (dwi) {
      d = axialSlice(dwi, k);
      [dLo, dHi] = displayRange(d.data, 1, 99);
    }
    cols.forEach(([, key], c) => {
      let p: Omit<Panel, "box">;
      switch (key) {
        case "dwi": p = { slice: d!, lut: GRAY_LUT, vmin: dLo, vmax: dHi, overlays: [] }; break;
        case "dwi_stroke_manual": p = { slice: d!, lut: GRAY_LUT, vmin: dLo, vmax: dHi, overlays: [{ contours: contoursOf(strokeLabel!, k), color: "#ff0000", linewidth: 1.0 }] }; break;
        case "dwi_stroke_pred": p = { slice: d!, lut: GRAY_LUT, vmin: dLo, vmax: dHi, overlays: [{ contours: contoursOf(strokePred!, k), color: "#00ff00", linewidth: 1.2 }] }; break;
        case "ttp": p = { slice: t, lut: JET_LUT, vmin: tLo, vmax: tHi, overlays: [] }; break;
        case "ttp_hp_manual": {
          const cs = contoursOf(hpLabel!, k);
          p = { slice: t, lut: JET_LUT, vmin: tLo, vmax: tHi, overlays: [{ contours: cs, color: "#ffffff", linewidth: 1.5 }, { contours: cs, color: "#000000", linewidth: 0.5, alpha: 0.7 }] };
          break;
        }
        case "ttp_hp_pred": p = { slice: t, lut: JET_LUT, vmin: tLo, vmax: tHi, overlays: [{ contours: contoursOf(hpPred, k), color: "#00ff00", linewidth: 1.5 }] }; break;
      }
      panels.push({ ...p, box: fitBox(cells[r][c], p.slice.width, p.slice.height) });
    });
  }

  // fig.text((c + 0.6)/n_cols, 0.95, title, ha="center", va="top", fontsize=12, bold) — figure
  // fraction coordinates, so the headers sit slightly right of each column's centre, as native.
  const texts = cols.map(([title], c) => {
    const probe = makeText(title, 12, ((c + 0.6) / nCols) * figW, 0);
    const top = (1 - 0.95) * figH;
    return { ...probe, baseline: top + probe.ascent };
  });
  return renderFigure(panels, texts);
}
